import { usePrefetchImage } from "@/components/progressive-img";
import { TarotCard } from "@/components/tarot-card";
import { getCloudValue, setCloudValue } from "@/lib/cloud-storage";
import { TarotDeckHandler, TarotDeckHandlerContext } from "@/lib/deck";
import { CardOfDaySchema, localizedCards, parseCod } from "@/lib/deck.helpers";
import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";

type COD = {
	name: string;
	isUpright: boolean;
	date: string;
};

export function CardOfTheDay() {
	const deckHandler = useContext(TarotDeckHandlerContext);
	const [shuffling, setShuffling] = useState(false);
	const [shuffleNumber, setShuffleNumber] = useState(0);
	const [cod, setCod] = useState<COD | null>();
	const [deck, setDeck] =
		useState<Awaited<ReturnType<typeof deckHandler.shuffle>>>();

	useEffect(() => {
		async function init() {
			window.Telegram.WebApp.SecondaryButton.setText("Перемешать");
			window.Telegram.WebApp.MainButton.setText("Показать!");

			const lastCodCardRaw = await getCloudValue("last-cod-card");
			if (lastCodCardRaw) {
				const lastCod = parseCod(lastCodCardRaw);
				setCod(lastCod ?? undefined);
			}
		}

		init();

		return () => {
			window.Telegram.WebApp.SecondaryButton.hide();
			window.Telegram.WebApp.MainButton.hide();
		};
	}, []);

	useEffect(() => {
		const handleShuffleSecondary = async () => {
			window.Telegram.WebApp.SecondaryButton.showProgress();

			setShuffling(true);

			const [deck] = await Promise.all([
				deckHandler.shuffle(),
				new Promise((r) => setTimeout(r, 1200)),
			]);

			setDeck(deck);
			setShuffling(false);
			setShuffleNumber((n) => n + 1);

			window.Telegram.WebApp.SecondaryButton.hideProgress();
		};

		const handleShow = async () => {
			window.Telegram.WebApp.MainButton.showProgress();

			const lastCodCardRaw = await getCloudValue("last-cod-card");
			const lastCod = lastCodCardRaw ? parseCod(lastCodCardRaw) : null;

			const noCodToday =
				!lastCod || !dayjs().isSame(dayjs(lastCod.date), "day");

			if (noCodToday) {
				const [topCard] = await deckHandler.getCardsByIdx(0);

				const newCodResult = CardOfDaySchema.parse({
					date: dayjs().format("YYYY-MM-DD"),
					name: topCard?.name,
					isUpright: topCard?.isUpright,
				});

				await setCloudValue(
					"last-cod-card",
					`${newCodResult.date}::${newCodResult.name}::${String(newCodResult.isUpright)}`,
				);

				fetch("/api/sendCod", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `tma ${window.Telegram.WebApp.initData}`,
					},
					body: JSON.stringify({
						cod: {
							title: localizedCards[newCodResult.name]?.ru,
							subtitle:
								newCodResult?.isUpright === false ? "перевернутая" : undefined,
							name: newCodResult.name,
							isUpright: newCodResult.isUpright,
						},
					}),
				});

				setCod(newCodResult);
			} else {
				setCod(lastCod);
			}

			window.Telegram.WebApp.MainButton.hideProgress();
		};

		window.Telegram.WebApp.SecondaryButton.onClick(handleShuffleSecondary);
		window.Telegram.WebApp.MainButton.onClick(handleShow);

		return () => {
			window.Telegram.WebApp.SecondaryButton.offClick(handleShuffleSecondary);
			window.Telegram.WebApp.MainButton.offClick(handleShow);
		};
	}, [deckHandler]);

	const codInitialized = cod !== undefined;
	const currentCodIsToday = Boolean(
		cod?.date && dayjs().isSame(dayjs(cod.date), "day"),
	);

	useEffect(() => {
		const showSecondary = codInitialized && !currentCodIsToday;
		const showMain = codInitialized && !currentCodIsToday && shuffleNumber > 0;

		if (
			showSecondary &&
			window.Telegram.WebApp.SecondaryButton.isVisible === false
		) {
			window.Telegram.WebApp.SecondaryButton.show();
		}

		if (
			showSecondary === false &&
			window.Telegram.WebApp.SecondaryButton.isVisible
		) {
			window.Telegram.WebApp.SecondaryButton.hide();
		}

		if (showMain && window.Telegram.WebApp.MainButton.isVisible === false) {
			window.Telegram.WebApp.MainButton.show();
		}

		if (showMain === false && window.Telegram.WebApp.MainButton.isVisible) {
			window.Telegram.WebApp.MainButton.hide();
		}
	}, [codInitialized, currentCodIsToday, shuffleNumber]);

	const topCard = deck?.unsafe_deck
		? TarotDeckHandler.getCardsFromDeck(deck?.unsafe_deck, 0).at(0)
		: undefined;

	usePrefetchImage(topCard ? `/deck/${topCard.name}-preview.webp` : undefined);
	usePrefetchImage(topCard ? `/deck/${topCard.name}.jpg` : undefined);

	return (
		<div className="py-8 px-[5vw] relative z-10">
			<div className="flex justify-between mb-3">
				<h1 className="text-2xl font-bold drop-shadow-[-2px_0px_8px_var(--color-yellow-400)]">
					Карта дня
					{currentCodIsToday && (
						<>
							<span>: {cod?.name && localizedCards[cod?.name]?.ru}</span>
							{cod?.isUpright === false && <span> (перевернутая)</span>}
						</>
					)}
				</h1>
				<div></div>
			</div>
			<div className="my-2 w-full max-w-4xl rounded-xl border border-amber-900/20 bg-amber-50/5 p-4 shadow-sm backdrop-blur-sm">
				<p className="text-sm mb-1">
					Каждый день вы можете вытягивать карту дня. А как её толковать — как
					напутствие, предсказание или повод для рефлексии — решать только вам
				</p>
			</div>
			<div className="relative w-[90vw]">
				{shuffling && (
					<div className="z-11 absolute inset-0 bg-white/70 flex items-center justify-center rounded-xl">
						<p className="text-xl">Перемешиваем...</p>
					</div>
				)}
				<TarotCard
					show={currentCodIsToday}
					name={cod?.name}
					isUpright={cod?.isUpright ?? true}
				/>
			</div>
		</div>
	);
}
